/**
 * fleet_control_request_store — 함대 차량들이 보낸 제어권 요청 목록.
 *
 * 다중 차량 모니터링 화면에서 "어느 차량이 제어를 요청했는지" 보여주기 위한 것이다.
 * 그 화면(FleetSelect)은 SceneInterpreter 바깥이라 제어 핸드셰이크 훅이 없다. 그래서
 * 훅에 매달지 않고 가벼운 싱글톤으로 둔다 — 이 저장소가 설정값에 쓰는 방식과 같다.
 *
 * 요청은 **차량당 하나**다. 문서(원격_모드_전환_시퀀스_정의_260818)상 차량은 응답이
 * 올 때까지 10초마다 REQ_INIT 을 재전송하므로, 같은 차량의 재전송은 갱신으로 처리하고
 * 갱신이 끊기면 스스로 사라지게 한다 — 차량이 꺼진 경우까지 화면에 남으면 안 된다.
 */
import { useSyncExternalStore } from "react";

/**
 * ControlRequest.request_state 값(brain_msgs.proto:241-245).
 *
 * mqtt_codec 을 import 하지 않는다 — 그러면 brain-proto 가 함께 끌려와서, 이 가벼운
 * 스토어를 쓰는 화면이 프로토 로딩에 묶인다(이 저장소에서 이미 한 번 당한 함정이다).
 * 값 두 개뿐이라 내재화가 싸다.
 */
const REQ_STATE_INIT = 1;

/**
 * 이만큼 재전송이 없으면 요청이 사라진 것으로 본다.
 *
 * 재전송 주기(10초)의 세 배 남짓 — 한두 번 놓쳐도 목록이 깜빡이지 않으면서, 차량이
 * 조용해지면 확실히 정리된다.
 */
export const FLEET_REQUEST_STALE_MS = 35_000;

export interface FleetControlRequest {
  vehicleId: string;
  controllerId: string;
  remoteType: number;
  /** 마지막으로 요청(재전송)을 받은 시각(ms). */
  atMs: number;
}

let requests: FleetControlRequest[] = [];
/** 콕핏이 이미 다른 차량을 제어 중인가 — 그동안은 목록을 받지 않는다. */
let suspended = false;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

/**
 * 함대 차량이 보낸 요청 하나를 반영한다.
 *
 * `REQ_INIT` 은 추가/갱신, `REQ_UNKNOWN` 은 제거(차량이 요청을 취소)다.
 */
export function applyFleetControlRequest(
  vehicleId: string,
  input: { requestState: number; controllerId?: string; remoteType?: number },
  nowMs: number = Date.now(),
): void {
  if (!vehicleId || suspended) {
    return;
  }

  if (input.requestState === REQ_STATE_INIT) {
    const next: FleetControlRequest = {
      vehicleId,
      controllerId: String(input.controllerId ?? ""),
      remoteType: Number(input.remoteType ?? 0),
      atMs: nowMs,
    };
    const index = requests.findIndex((r) => r.vehicleId === vehicleId);
    if (index >= 0) {
      requests = requests.map((r, i) => (i === index ? next : r));
    } else {
      requests = [...requests, next];
    }
    emit();
    return;
  }

  // REQ_UNKNOWN — 차량이 요청을 거둬들였다.
  if (requests.some((r) => r.vehicleId === vehicleId)) {
    requests = requests.filter((r) => r.vehicleId !== vehicleId);
    emit();
  }
}

/** 갱신이 끊긴 요청을 걷어낸다. 화면 쪽에서 주기적으로 부른다. */
export function pruneFleetControlRequests(nowMs: number = Date.now()): void {
  const kept = requests.filter((r) => nowMs - r.atMs <= FLEET_REQUEST_STALE_MS);
  if (kept.length !== requests.length) {
    requests = kept;
    emit();
  }
}

/** 요청 하나를 처리 완료로 지운다(운영자가 골라서 연결한 경우). */
export function clearFleetControlRequest(vehicleId: string): void {
  if (!requests.some((r) => r.vehicleId === vehicleId)) {
    return;
  }
  requests = requests.filter((r) => r.vehicleId !== vehicleId);
  emit();
}

/**
 * 목록 수신을 멈추거나 재개한다.
 *
 * 이미 다른 차량을 제어 중이면 요청을 무시한다 — 수락하면 지금 제어를 놓게 되므로,
 * 주행 중에 그 선택지를 화면에 띄우는 것 자체가 위험하다.
 */
export function setFleetControlRequestsSuspended(next: boolean): void {
  if (suspended === next) {
    return;
  }
  suspended = next;
  if (suspended && requests.length > 0) {
    requests = [];
  }
  emit();
}

export function getFleetControlRequests(): FleetControlRequest[] {
  return requests;
}

export function subscribeFleetControlRequests(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

export function useFleetControlRequests(): FleetControlRequest[] {
  return useSyncExternalStore(
    subscribeFleetControlRequests,
    getFleetControlRequests,
    () => EMPTY,
  );
}

const EMPTY: FleetControlRequest[] = [];

/** 테스트용 — 모듈 상태를 비운다. */
export function resetFleetControlRequestsForTest(): void {
  requests = [];
  suspended = false;
  emit();
}
